/**
 * Live-Update (Flori 25.10.2026, `docs/NUTZUNGSSZENARIEN.md`): Opt-in des Kunden. Nur mit eingeschaltetem Live-Update
 * schreibt PAT in Beschreibungen; automatisch nur Galerieblöcke, vollständige Beschreibungen nur auf Auftrag je
 * Angebot (Artikelseite). Live-Vorschau je Angebotsnummer im Blockmodus (nur dev/Sandbox); Auto-Einfügen je Site und
 * Galerie-Tabelle folgen (D7, Ports bei Codex).
 * Seit 31.10.2026 (Flori) wieder sichtbar, auch in der BETA. Den Schalter bedient dort der Inhaber, wenn die API den
 * Write-through ins bisherige PAT meldet (`liveUpdate.writeThrough`); sonst bleiben Schalter und Live-Vorschau eingefroren.
 * Seit 01.11.2026 (Flori) Teil der Seite „eBay & Live-Update“: Cross-Selling braucht Live-Update, und Einschalten geht nur
 * mit gültiger eBay-Verbindung (nach Ablauf erst neu verbinden, dann bewusst wieder einschalten).
 */
import { Alert, Badge, Button, Card, Group, Stack, Switch, Table, Text, Textarea, Title } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { MAX_LIVE_UPDATE_ITEMS, type LiveUpdateItemOutcome, type StatusResponse } from '../../../shared/contract.js';
import { ApiRequestError } from '../api/client.js';
import { useFrozen, useServices } from '../services.js';
import { dateTime, t } from '../texts.js';
import { LiveBlockPreviewByNumber } from './live-block-preview.js';
import { useMe } from './tenant-layout.js';

/** Schreibgeschützt (eingefroren oder BETA ohne Write-through): kein Schalter, keine Live-Vorschau. */
function useReadOnly(tenantId: string) {
  const membership = useMe().data?.memberships.find(m => m.tenantId === tenantId);
  return { role: membership?.role, readOnly: useFrozen() || membership?.beta === true };
}

/** Schalter mit Erklärung; daneben steht auf der Seite die eBay-Verbindung, von der das Einschalten abhängt. */
export function LiveUpdateCard({ tenantId, status }: { tenantId: string; status: StatusResponse }) {
  const { api } = useServices();
  const queryClient = useQueryClient();
  const { role, readOnly } = useReadOnly(tenantId);
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.setLiveUpdate(tenantId, enabled, status.liveUpdate.changedAt ?? null),
    // Neuer Stand sofort (sonst ginge ein zweiter Klick mit dem alten `changedAt` als falscher Konflikt hinaus).
    onSuccess: ({ liveUpdate }) => queryClient.setQueryData<StatusResponse>(['status', tenantId], old => (old ? { ...old, liveUpdate } : old)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['status', tenantId] }),
  });
  const { liveUpdate } = status;
  const migrated = status.migrationState === 'migriert';
  // BETA mit Write-through (Flori 31.10.2026): schaltet PAT4 und das bisherige PAT zusammen; sonst erst nach dem Umzug.
  const writeThrough = liveUpdate.writeThrough === true;
  // Wie die API: einschalten nur mit gültigem Token, ausschalten immer.
  const ebayUsable = status.ebay.state === 'ok' || status.ebay.state === 'expiring';
  const canToggle = role === 'owner' && (liveUpdate.allowed || liveUpdate.enabled) && (writeThrough || (!readOnly && migrated))
    && (liveUpdate.enabled || ebayUsable);
  // Vor dem Umzug ohne Write-through gilt der Schalter im bisherigen PAT; ohne Tarif-Recht frischt niemand etwas auf.
  const detail = !(migrated || writeThrough)
    ? undefined
    : !liveUpdate.enabled
      ? t('liveUpdate.off')
      : !liveUpdate.allowed
        ? t('liveUpdate.onWithoutRight')
        : liveUpdate.changedAt
          ? t('liveUpdate.on', { date: dateTime(liveUpdate.changedAt), days: liveUpdate.intervalDays })
          : undefined;
  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={4}>{t('liveUpdate.title')}</Title>
          <Badge color={liveUpdate.enabled ? 'green' : 'gray'}>{t(liveUpdate.enabled ? 'liveUpdate.badge.on' : 'liveUpdate.badge.off')}</Badge>
        </Group>
        <Text size="sm">{t('liveUpdate.intro')}</Text>
        {/* Beschriftung nennt den Zustand (Flori 01.11.2026): am Schalter allein war „an“ oder „aus“ schwer zu erkennen. */}
        <Switch
          size="md"
          color="green"
          label={t(liveUpdate.enabled ? 'liveUpdate.switch.on' : 'liveUpdate.switch.off')}
          {...(detail ? { description: detail } : {})}
          checked={liveUpdate.enabled}
          disabled={!canToggle || toggle.isPending}
          onChange={e => toggle.mutate(e.currentTarget.checked)}
          styles={{ label: { fontWeight: 600 } }}
        />
        {!liveUpdate.enabled && !ebayUsable && liveUpdate.allowed && <Alert color="yellow">{t('liveUpdate.tokenRequired')}</Alert>}
        {!liveUpdate.allowed && <Alert color="yellow">{t('liveUpdate.notAllowed')}</Alert>}
        {!writeThrough && !migrated && <Alert color="blue">{t('liveUpdate.notMigrated')}</Alert>}
        {role !== 'owner' && (
          <Text size="xs" c="dimmed">
            {t('liveUpdate.ownerOnly')}
          </Text>
        )}
        {toggle.isError && (
          <Alert color="red" role="alert">
            {toggle.error instanceof ApiRequestError && toggle.error.detail ? toggle.error.detail : t('common.error')}
          </Alert>
        )}
        <Text size="xs" c="dimmed">
          {t('liveUpdate.rules')}
        </Text>
      </Stack>
    </Card>
  );
}

/** Angebotsnummer wie die API (`EbayItemNumber`). */
const ITEM_NUMBER = /^[1-9][0-9]{8,18}$/;
/** eBay-Link (`/itm/<Nummer>` oder `/itm/<Titel>/<Nummer>`, `?item=<Nummer>`). */
const ITEM_LINK = /\/itm\/(?:[^/?#\s]+\/)?([1-9][0-9]{8,18})(?:[/?#]|$)|[?&]item=([1-9][0-9]{8,18})(?:&|$)/;

export interface ParsedItemNumbers {
  /** Erkannte Nummern in Eingabereihenfolge, jede nur einmal. */
  readonly ids: readonly string[];
  readonly duplicates: number;
  /** Nicht erkannte Teile, gekürzt. */
  readonly invalid: readonly string[];
}

/**
 * Freitext aus dem Kunden-Feld: Trennzeichen egal (Leerzeichen, Zeilen, Komma, Semikolon, Tab, senkrechter Strich), auch
 * kopierte eBay-Links und Anführungszeichen. Doppelte zählen einmal.
 */
export function parseItemNumbers(text: string): ParsedItemNumbers {
  const ids: string[] = [];
  const invalid: string[] = [];
  let duplicates = 0;
  for (const raw of text.split(/[\s,;|]+/)) {
    const token = raw.replace(/^["'„“”‚‘’(\[<]+|["'„“”‚‘’)\]>.:]+$/g, '');
    if (!token) continue;
    const link = token.includes('/') || token.includes('?') ? ITEM_LINK.exec(token) : null;
    const id = link ? (link[1] ?? link[2]) : ITEM_NUMBER.test(token) ? token : undefined;
    if (!id) invalid.push(token.length > 32 ? `${token.slice(0, 31)}…` : token);
    else if (ids.includes(id)) duplicates++;
    else ids.push(id);
  }
  return { ids, duplicates, invalid };
}

const OUTCOME_COLOR: Record<LiveUpdateItemOutcome, string> = { queued: 'green', 'not-found': 'red', 'item-ended': 'gray', ambiguous: 'orange' };

/**
 * „eBay-Angebote updaten“ (Flori 02.11.2026, wie im bisherigen PAT „Einzelnes ebay Angebot updaten“ unter Cross-Selling →
 * Live-Update, hier mehrere auf einmal): stellt Angebote in die Live-Update-Warteschlange des bisherigen PAT, das bis zum
 * Umzug die Beschreibungen schreibt. Vorschau der erkannten Nummern vor dem Absenden. Nur, wo die API es anbietet
 * (`liveUpdate.singleItem`), ab Bearbeiter.
 */
export function LiveUpdateItemCard({ tenantId, status }: { tenantId: string; status: StatusResponse }) {
  const { api } = useServices();
  const { role } = useReadOnly(tenantId);
  const [value, setValue] = useState('');
  const queue = useMutation({ mutationFn: (ids: readonly string[]) => api.queueLiveUpdateItems(tenantId, ids) });
  const parsed = useMemo(() => parseItemNumbers(value), [value]);
  if (status.liveUpdate.singleItem !== true || (role !== 'owner' && role !== 'editor')) return null;
  const enabled = status.liveUpdate.enabled;
  const tooMany = parsed.ids.length > MAX_LIVE_UPDATE_ITEMS;
  const canSubmit = enabled && parsed.ids.length > 0 && !tooMany && !queue.isPending;
  const results = queue.data?.results;
  const queued = results?.filter(result => result.outcome === 'queued').length ?? 0;
  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <Title order={4}>{t('liveUpdate.item.title')}</Title>
        <Text size="sm">{t('liveUpdate.item.intro')}</Text>
        <Textarea
          label={t('liveUpdate.item.label')}
          description={t('liveUpdate.item.hint')}
          placeholder={'123456789012\n223456789012, 323456789012'}
          autosize
          minRows={4}
          maxRows={12}
          maxLength={20000}
          spellCheck={false}
          value={value}
          disabled={!enabled}
          onChange={e => {
            setValue(e.currentTarget.value);
            queue.reset();
          }}
        />
        {value.trim() !== '' && (
          <Stack gap={6} aria-label={t('liveUpdate.item.preview')}>
            <Text size="sm" fw={600}>
              {t(parsed.ids.length === 1 ? 'liveUpdate.item.found.one' : 'liveUpdate.item.found', { count: parsed.ids.length })}
              {parsed.duplicates > 0 ? ` ${t('liveUpdate.item.duplicates', { count: parsed.duplicates })}` : ''}
            </Text>
            {parsed.ids.length > 0 && (
              <Group gap={6}>
                {parsed.ids.map(id => (
                  <Badge key={id} variant="light" color="blue" tt="none">
                    {id}
                  </Badge>
                ))}
              </Group>
            )}
            {parsed.invalid.length > 0 && (
              <Text size="xs" c="red">
                {t('liveUpdate.item.invalid', { list: parsed.invalid.slice(0, 10).join(', ') + (parsed.invalid.length > 10 ? ' …' : '') })}
              </Text>
            )}
            {tooMany && <Alert color="yellow">{t('liveUpdate.item.tooMany', { max: MAX_LIVE_UPDATE_ITEMS })}</Alert>}
          </Stack>
        )}
        <Group>
          <Button disabled={!canSubmit} loading={queue.isPending} onClick={() => queue.mutate(parsed.ids)}>
            {parsed.ids.length > 1 ? t('liveUpdate.item.submit.many', { count: parsed.ids.length }) : t('liveUpdate.item.submit')}
          </Button>
        </Group>
        {!enabled && (
          <Text size="xs" c="dimmed">
            {t('liveUpdate.item.off')}
          </Text>
        )}
        {results && (
          <Stack gap={6}>
            <Alert color={queued === results.length ? 'green' : queued > 0 ? 'yellow' : 'red'} role="status">
              {t('liveUpdate.item.done', { queued, count: results.length })}
            </Alert>
            {results.length > 1 || queued === 0 ? (
              <Table withRowBorders={false} verticalSpacing={2} aria-label={t('liveUpdate.item.results')}>
                <Table.Tbody>
                  {results.map(result => (
                    <Table.Tr key={result.ebayItemId}>
                      <Table.Td>{result.ebayItemId}</Table.Td>
                      <Table.Td>
                        <Badge variant="light" color={OUTCOME_COLOR[result.outcome]} tt="none">
                          {t(`liveUpdate.item.outcome.${result.outcome}`)}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : null}
          </Stack>
        )}
        {queue.isError && (
          <Alert color="red" role="alert">
            {queue.error instanceof ApiRequestError && queue.error.detail ? queue.error.detail : t('common.error')}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/** Live-Vorschau je Angebotsnummer (Blockmodus); nur wo nicht schreibgeschützt. */
export function LiveUpdatePreview({ tenantId, status }: { tenantId: string; status: StatusResponse }) {
  const { role, readOnly } = useReadOnly(tenantId);
  if (readOnly) return null;
  return (
    <Card withBorder padding="lg">
      <LiveBlockPreviewByNumber tenantId={tenantId} editable={status.capabilities.editors && (role === 'owner' || role === 'editor')} />
    </Card>
  );
}
